import { DecimalPipe, PercentPipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { PhxDateTimeFormatPipe, PhxFormatTypes, PhxVariationTypes } from 'phx-core';

import { FullNamePipe } from '@pipes/full-name';
import { CoordinatePipe } from '@pipes/coordinate';
import { SpeedValuePipe } from '@pipes/speed';
import { DistancePipe } from '@pipes/distance';
import { DurationPipe } from '@pipes/duration';
import { FuelTypePipe } from '@pipes/fuel-type';
import { HumanizePipe } from '@pipes/humanize';
import { IftaDistancePipe } from '@pipes/ifta/distance';
import { IftaJurisdictionPipe } from '@pipes/ifta/jurisdiction';
import { IftaOdometerPipe } from '@pipes/ifta/odometer';
import { MetricUnitsPipe } from '@pipes/metric-units';
import { MomentPipe } from '@pipes/moment';
import { VolumePipe } from '@pipes/volume';
import { DutyStatusDurationPipe } from '@pipes/duty-status-duration';
import { WeightUnitPipe } from '@pipes/weight-unit';
import { DvirStatusPipe } from '@pipes/dvir-status';
import { WifiStatusPipe } from '@pipes/wifi-status';
import { HosDurationPipe } from '@pipes/hos-duration';
import { StatePipe } from '@pipes/states';
import { FileSizePipe } from '@pipes/file-size';
import { FuelEconomyPipe } from '@pipes/fuel-economy';
import { EvEconomyPipe } from './ev-economy.pipe';
import { SecondaryKeyPipe } from './secondary-key.pipe';
import { MetricDistanceUnitsPipe } from './metric-distance-units.pipe';

import { TableUtilsService } from '@reports/report/table/table-utils.service';
import { Session } from '@services/session';

import { getCycle } from '@app/cycle/cycles';
import { IMoment, moment } from '@lib/moment';
import { logger } from '@lib/datadog';
import { calculateDistancePerHundredUnit, formatAcceleration } from '@lib/safety/safety';
import { phoneNumberToIntlFormat } from '@lib/utils';

import {
  ICondition,
  ISecondaryTextConfig,
  ITableColumn,
  ITableData,
  TranslationObjectType,
} from '@reports/report/config-report.interface';
import { ConfigConditionService } from '@reports/report/config-condition.service';

export enum FORMAT_PIPES {
  DATE_TIME = 'date_time',
  DATE_TIME_FORMAT = 'date_time_format',
  DISTANCE = 'distance',
  WEIGHT_UNIT = 'weight_unit',
  LOCAL_TIME_ZONE = 'local_time_zone',
  NUMBER = 'number',
  IFTA_DISTANCE = 'ifta_distance',
  IFTA_JURISDICTIONS = 'ifta_jurisdiction',
  IFTA_ODOMETER = 'ifta_odometer',
  FUEL_TYPE = 'fuel_type',
  METRICS_UNIT = 'metric_units',
  VOLUME = 'volume',
  COORDINATE = 'coordinate',
  EXTRA_TEXT = 'extra_text',
  EXTRA_TEXT_PLURAL = 'extra_text_plural',
  DURATION = 'duration',
  HUMANIZE = 'humanize',
  MAP_ENUM = 'map_enum',
  FULL_NAME = 'full_name',
  DUTY_STATUS_DURATION = 'duty_status_duration',
  ADD_NUMBERS_FROM_KEYS = 'add_numbers_from_keys',
  CYCLE = 'cycle',
  DVIR_STATUS = 'dvir_status',
  WIFI_STATUS = 'wifi_status',
  SPEED = 'speed',
  HOS_DURATION = 'hos_duration',
  STATE_PIPE = 'STATE_PIPE',
  FILE_SIZE = 'file-size',
  FUEL_ECONOMY = 'fuel_economy',
  ACCELERATION = 'acceleration',
  EV_ECONOMY = 'ev_economy',
  SECONDARY_KEY = 'secondary_key',
  METRIC_DISTANCE_UNITS = 'metric_distance_units',
  CALCULATE_DISTANCE_PER_HUNDRED_UNIT = 'calculate_distance_per_hundred_unit',
  SPEEDING_RATE = 'speeding_rate',
  PHONE_NUMBER_FORMAT = 'phone_number_format',
  PERCENTAGE_FORMAT = 'percentage_format',
  BOOLEAN_YES_NO = 'boolean_yes_no',
}

/**
 * @property user_user_time_zone: converts the given time into user time
 * @property time_zone_key: converts the given time into the timezone coming in the mentioned key.
 */
export interface IDateTimePipeConfig {
  name: FORMAT_PIPES.DATE_TIME;
  use_user_time_zone?: boolean;
  time_zone_key?: string;
  format?: string;
}

export interface IDateTimeFormatPipeConfig {
  name: FORMAT_PIPES.DATE_TIME_FORMAT;
  format_type: PhxFormatTypes;
  variation: PhxVariationTypes;
  use_user_time_zone?: boolean;
  time_zone_key?: string;
}

export interface IFuelEconomyPipeConfig {
  name: FORMAT_PIPES.FUEL_ECONOMY;
  check_unit_system_preference?: boolean;
  fraction_size?: number;
}

export interface ILocalTimeZonePipeConfig {
  name: FORMAT_PIPES.LOCAL_TIME_ZONE;
}

export interface INumberPipeConfig {
  name: FORMAT_PIPES.NUMBER;
  format?: string;
  format_minus_one_to_null?: boolean;
}

export interface IDvirStatusPipeConfig {
  name: FORMAT_PIPES.DVIR_STATUS;
  key: string;
}

export interface IWeightUnitPipeConfig {
  name: FORMAT_PIPES.WEIGHT_UNIT;
  current_unit: 'lb' | 'kg';
  format: string;
  check_unit_system_preference?: boolean;
}

export interface IVolumePipeConfig {
  name: FORMAT_PIPES.VOLUME;
  check_unit_system_preference?: boolean;
}

export interface IIftaOdometerPipeConfig {
  name: FORMAT_PIPES.IFTA_ODOMETER;
  check_unit_system_preference?: boolean;
}

export interface ICoordinatePipeConfig {
  name: FORMAT_PIPES.COORDINATE;
  multiple_coordinate_seperator: string;
}

export interface IHumanizePipeConfig {
  name: FORMAT_PIPES.HUMANIZE;
}

export interface IMapEnumPipeConfig {
  name: FORMAT_PIPES.MAP_ENUM;
  mappings: { [from: string]: TranslationObjectType; };
}

export interface IIftaDistancePipeConfig {
  name: FORMAT_PIPES.IFTA_DISTANCE;
  check_unit_system_preference?: boolean;
  fraction_size?: number;
}

export interface IDistancePipeConfig {
  name: FORMAT_PIPES.DISTANCE;
  check_unit_system_preference?: boolean;
  check_unit_data_preference_key?: string;
  do_round?: boolean;
  format?: string;
}

export interface ISpeedPipeConfig {
  name: FORMAT_PIPES.SPEED;
  user_metric_units?: boolean;
  current_data_units?: string;
  decimal_places?: number;
  check_unit_system_preference?: boolean;
}

export interface IMetricsUnitPipeConfig {
  name: FORMAT_PIPES.METRICS_UNIT;
  show_quantity?: boolean;
  check_unit_system_preference?: boolean;
  check_unit_data_preference_key?: string;
  type?: string;
  add_prefix_system_unit?: boolean;
  add_postfix_system_unit?: boolean;
}

export interface IIftaJurisdictionsPipeConfig {
  name: FORMAT_PIPES.IFTA_JURISDICTIONS;
}

export interface IFuelTypePipeConfig {
  name: FORMAT_PIPES.FUEL_TYPE;
}

export interface IDutyStatusDurationPipeConfig {
  name: FORMAT_PIPES.DUTY_STATUS_DURATION;
}

export interface ICyclePipeConfig {
  name: FORMAT_PIPES.CYCLE;
}

export interface IAddNumbersFromKeysPipeConfig {
  name: FORMAT_PIPES.ADD_NUMBERS_FROM_KEYS,
  keys: string[],
}

export interface IWifiStatusPipeConfig {
  name: FORMAT_PIPES.WIFI_STATUS;
}

export interface IBooleanYesNoPipeConfig {
  name: FORMAT_PIPES.BOOLEAN_YES_NO;
}

export interface IExtraTextPipeConfig {
  name: FORMAT_PIPES.EXTRA_TEXT;
  text?: string;
  add_unit_system?: string;
  add_text_from_data_key?: string;
  prepend_text_for_data_key?: string;
  append_text_for_data_key?: string;
  unit_type?: string;
  prepend?: boolean;
  append?: boolean;
  check_unit_system_preference?: boolean;
  check_unit_data_preference_key?: string;
  add_no_space?: boolean;
  add_new_line?: boolean;
  add_time_zone?: boolean;
  type?: string;
  moment_format?: string;
  show_if_null_base_text?: boolean;
  show_condition?: ICondition;
}

export interface IExtraTextPluralPipeConfig {
  name: FORMAT_PIPES.EXTRA_TEXT_PLURAL;
  text?: {
    zero_text: TranslationObjectType;
    singular_text: TranslationObjectType;
    plural_text: TranslationObjectType;
  };
  prepend?: boolean;
  append?: boolean;
  add_no_space?: boolean;
  show_condition?: ICondition;
}

export interface IDurationPipeConfig {
  name: FORMAT_PIPES.DURATION,
  time_in_sec?: number,
  show_seconds?: boolean,
  show_days?: boolean,
  show_month?: boolean,
  show_year?: boolean,
  round_off?: boolean,
  use_short_hand_unit?: boolean,
  post_fix_string?: string,
  hide_minutes_for_three_digit_hour_value?: boolean,
  show_week?: boolean,
  show_mins?: boolean,
  less_than_unit?: string,
  multiply_time?: number,
  hide_minutes_in_hour_value?: boolean,
  show_leading_zero?: boolean,
  use_compact_format?: boolean,
  show_zero_value?: boolean,
}

export interface IFullNamePipeConfig {
  name: FORMAT_PIPES.FULL_NAME,
  first_name_key: string;
  last_name_key: string;
  append_deactivated?: {
    deactivated_key: string;
  };
}

export interface IDutyStatusPipeConfig {
  name: FORMAT_PIPES.DUTY_STATUS_DURATION;
}

export interface IHosDurationPipeConfig {
  name: FORMAT_PIPES.HOS_DURATION;
  show_seconds?: boolean;
}

export interface IStatePipeConfig {
  name: FORMAT_PIPES.STATE_PIPE;
}

export interface IFileSizePipeConfig {
  name: FORMAT_PIPES.FILE_SIZE;
}

export interface IPercentPipeConfig {
  name: FORMAT_PIPES.PERCENTAGE_FORMAT;
  format?: string;
}

interface IAccelerationPipeConfig {
  name: FORMAT_PIPES.ACCELERATION;
  event_key: string;
}

interface IEvEconomyPipeConfig {
  name: FORMAT_PIPES.EV_ECONOMY;
  check_unit_system_preference?: boolean;
  reverse?: boolean;
  decimal_places?: number;
}

interface ISecondaryKeyPipeConfig {
  name: FORMAT_PIPES.SECONDARY_KEY;
  keys: string[];
}

interface IMetricDistanceUnitsPipeConfig {
  name: FORMAT_PIPES.METRIC_DISTANCE_UNITS;
}

interface ICalculateDistancePerHundredUnitPipeConfig {
  name: FORMAT_PIPES.CALCULATE_DISTANCE_PER_HUNDRED_UNIT;
  event_key: string;
  distance_key: string;
}

interface ISpeedingRate {
  name: FORMAT_PIPES.SPEEDING_RATE;
  driving_time_key: string;
  format: string;
}

export interface IPhoneNumberFormatPipeConfig {
  name: FORMAT_PIPES.PHONE_NUMBER_FORMAT;
  phone_key: string;
  phone_country_code_key: string;
}

export type IFormatConfig =
  IDateTimePipeConfig |
  ILocalTimeZonePipeConfig |
  INumberPipeConfig |
  IWeightUnitPipeConfig |
  IVolumePipeConfig |
  IIftaDistancePipeConfig |
  IDistancePipeConfig |
  IMetricsUnitPipeConfig |
  IIftaJurisdictionsPipeConfig |
  IFuelTypePipeConfig |
  IExtraTextPipeConfig |
  IIftaOdometerPipeConfig |
  ICoordinatePipeConfig |
  IHumanizePipeConfig |
  IMapEnumPipeConfig |
  IDurationPipeConfig |
  IFullNamePipeConfig |
  IDutyStatusDurationPipeConfig |
  IAddNumbersFromKeysPipeConfig |
  IExtraTextPluralPipeConfig |
  ICyclePipeConfig |
  IDvirStatusPipeConfig |
  ISpeedPipeConfig |
  IWifiStatusPipeConfig |
  IDutyStatusPipeConfig |
  IWifiStatusPipeConfig |
  IHosDurationPipeConfig |
  IStatePipeConfig |
  IFileSizePipeConfig |
  IDateTimeFormatPipeConfig |
  IFuelEconomyPipeConfig |
  IAccelerationPipeConfig |
  IEvEconomyPipeConfig |
  ISecondaryKeyPipeConfig |
  IMetricDistanceUnitsPipeConfig |
  ICalculateDistancePerHundredUnitPipeConfig |
  ISpeedingRate |
  IPhoneNumberFormatPipeConfig |
  IPercentPipeConfig |
  IBooleanYesNoPipeConfig;

@Pipe({ name: 'formatData' })
export class FormatDataPipe implements PipeTransform {
  public metricUnits;
  private currentLanguage: string;
  private fuelEconomyUnit: string;

  constructor(
    private session: Session,
    private tableUtilsService: TableUtilsService,
    private momentPipe: MomentPipe,
    private numberPipe: DecimalPipe,
    private iftaDistancePipe: IftaDistancePipe,
    private distancePipe: DistancePipe,
    private metricUnitsPipe: MetricUnitsPipe,
    private volumePipe: VolumePipe,
    private iftaJurisdictionPipe: IftaJurisdictionPipe,
    private fuelTypePipe: FuelTypePipe,
    private wifiStatusPipe: WifiStatusPipe,
    private humanizePipe: HumanizePipe,
    private iftaOdometerPipe: IftaOdometerPipe,
    private coordinatePipe: CoordinatePipe,
    private durationPipe: DurationPipe,
    private fullNamePipe: FullNamePipe,
    private dutyStatusDurationPipe: DutyStatusDurationPipe,
    private weightUnitPipe: WeightUnitPipe,
    private dvirStatusPipe: DvirStatusPipe,
    private speedValuePipe: SpeedValuePipe,
    private hosDuration: HosDurationPipe,
    private statePipe: StatePipe,
    private fileSizePipe: FileSizePipe,
    private phxDateTimeFormat: PhxDateTimeFormatPipe,
    private fuelEconomyPipe: FuelEconomyPipe,
    private evEconomyPipe: EvEconomyPipe,
    private secondaryKeyPipe: SecondaryKeyPipe,
    private metricDistanceUnitsPipe: MetricDistanceUnitsPipe,
    private percentPipe: PercentPipe,
    private configConditionService: ConfigConditionService,
  ) {
    this.metricUnits = session.getUserMetricUnits();
    this.currentLanguage = this.session.getUserLanguage();
    this.fuelEconomyUnit = session.getUserFuelEconomyUnits();
  }

  public transform(
    text: string,
    formatConfig: IFormatConfig[],
    data: ITableData = {},
    columnConfig?: ITableColumn | ISecondaryTextConfig,
  ) {
    if (!formatConfig || formatConfig.length === 0) return text;
    const showIfNullBaseText = formatConfig.find((obj: any) => obj.show_if_null_base_text);
    if (!showIfNullBaseText && (text === null || text === '' || text === undefined)) return text;

    let transformedText: string = text;
    for (const item of formatConfig) {
      let metricUnit: boolean;
      if ((item as any)?.check_unit_system_preference) {
        metricUnit = this.metricUnits;
      } else if ((item as any)?.check_unit_data_preference_key) {
        metricUnit = data[(item as any).check_unit_data_preference_key];
      }

      switch (item.name) {
      case FORMAT_PIPES.DATE_TIME:
        if (item.time_zone_key) {
          const dateTimeValue: IMoment = moment(transformedText).mapTz(data[item.time_zone_key]);
          transformedText = this.momentPipe.transform(dateTimeValue, item.format);
        } else {
          transformedText = this.momentPipe.transform(moment(transformedText), item.format, item.use_user_time_zone);
        }
        break;
      case FORMAT_PIPES.DATE_TIME_FORMAT:
        let timezone: string;
        if (item.use_user_time_zone) {
          timezone = this.session.getUserTimeZone();
        } else if (item.time_zone_key) {
          timezone = data[item.time_zone_key];
        }

        transformedText = this.phxDateTimeFormat.transform(
          transformedText,
          item.format_type,
          item.variation,
          timezone,
        );
        break;
      case FORMAT_PIPES.LOCAL_TIME_ZONE:
        transformedText = moment.dateInLocalTimeZone(transformedText);
        break;
      case FORMAT_PIPES.NUMBER:
        if (item.format_minus_one_to_null && transformedText == '-1') {
          transformedText = '--';
        } else {
          transformedText = this.numberPipe.transform(transformedText, item.format);
        }
        break;
      case FORMAT_PIPES.PERCENTAGE_FORMAT:
        transformedText = this.percentPipe.transform(transformedText, item.format);
        break;
      case FORMAT_PIPES.VOLUME:
        transformedText = this.volumePipe.transform(transformedText, metricUnit);
        break;
      case FORMAT_PIPES.DVIR_STATUS:
        transformedText = this.dvirStatusPipe.transform(transformedText, data[item.key]?.length);
        break;
      case FORMAT_PIPES.COORDINATE:
        if (item.multiple_coordinate_seperator) {
          transformedText = transformedText
            .split(item.multiple_coordinate_seperator)
            .map((coordinate: string) => this.coordinatePipe.transform(coordinate))
            .join(item.multiple_coordinate_seperator);
        } else {
          transformedText = this.coordinatePipe.transform(transformedText).toString();
        }
        break;
      case FORMAT_PIPES.DURATION:
        if (item.multiply_time) {
          transformedText = (parseFloat(transformedText) * item.multiply_time) + '';
        }

        transformedText = this.durationPipe.transform(
          parseFloat(transformedText),
          item.show_seconds,
          item.show_days,
          item.show_month,
          item.show_year,
          item.round_off,
          item.use_short_hand_unit,
          item.post_fix_string,
          item.hide_minutes_for_three_digit_hour_value,
          item.show_week,
          item.show_mins,
          item.less_than_unit,
          item.hide_minutes_in_hour_value,
          item.show_leading_zero,
          item.use_compact_format,
          item.show_zero_value,
        );
        break;
      case FORMAT_PIPES.DUTY_STATUS_DURATION:
        transformedText = this.dutyStatusDurationPipe.transform(transformedText) + '';
        break;
      case FORMAT_PIPES.IFTA_ODOMETER:
        transformedText = this.iftaOdometerPipe.transform(transformedText, metricUnit);
        break;
      case FORMAT_PIPES.IFTA_DISTANCE:
        transformedText = this.iftaDistancePipe.transform(transformedText, metricUnit, item.fraction_size);
        break;
      case FORMAT_PIPES.DISTANCE:
        transformedText = this.distancePipe.transform(
          +transformedText,
          this.metricUnits,
          item.do_round,
          item.format,
        );
        break;
      case FORMAT_PIPES.SPEED:
        transformedText = this.speedValuePipe.transform(
          parseFloat(transformedText), metricUnit, item.current_data_units, item.decimal_places,
        ) + '';
        break;
      case FORMAT_PIPES.METRICS_UNIT:
        transformedText = this.metricUnitsPipe.transform(metricUnit, transformedText, item.type, item.show_quantity);
        break;
      case FORMAT_PIPES.IFTA_JURISDICTIONS:
        transformedText = this.iftaJurisdictionPipe.transform(transformedText);
        break;
      case FORMAT_PIPES.FUEL_TYPE:
        transformedText = this.fuelTypePipe.transform(transformedText);
        break;
      case FORMAT_PIPES.WIFI_STATUS:
        transformedText = this.wifiStatusPipe.transform(transformedText);
        break;
      case FORMAT_PIPES.EXTRA_TEXT:
        if (item.show_condition && item.show_condition?.name) {
          if (
            !this.configConditionService.evaluateCondition(
              item.show_condition.name,
              null,
              [data],
              0,
              item.show_condition.args || null,
            )
          ) {
            break;
          }
        }
        let textToAdd: string;
        if (item.add_unit_system && transformedText && transformedText !== '--') {
          textToAdd = this.metricUnitsPipe.transform(metricUnit, text, item.type);
        } else if (item.add_text_from_data_key && data[item.add_text_from_data_key]) {
          // TODO: Investigate to refactor this to textToAdd.
          if (item.prepend_text_for_data_key) {
            textToAdd = item.prepend_text_for_data_key + ' ' + data[item.add_text_from_data_key];
          } else if (item.append_text_for_data_key) {
            textToAdd = data[item.add_text_from_data_key] + ' ' + item.append_text_for_data_key;
          } else {
            textToAdd = data[item.add_text_from_data_key];
          }
        } else {
          textToAdd = item.text;
        }

        if (item.add_time_zone) {
          textToAdd = '(' + this.momentPipe.transform(moment(), 'Zone') + ')';
        }

        if (item.moment_format) {
          textToAdd = this.momentPipe.transform(moment(textToAdd), item.moment_format);
        }

        if (textToAdd) {
          textToAdd = item.add_new_line ? `${textToAdd}\n` : textToAdd;

          if (item.prepend) {
            transformedText = textToAdd + (item.add_no_space ? '' : ' ') + transformedText;
          } else if (item.append) {
            transformedText = transformedText + (item.add_no_space ? '' : ' ') + textToAdd;
          }
        }
        break;
      case FORMAT_PIPES.EXTRA_TEXT_PLURAL:

        if (item.show_condition && item.show_condition?.name) {
          if (!this.configConditionService.evaluateCondition(
          item.show_condition.name,
          null, [data], 0, item.show_condition.args || null)) {
            break;
          }
        }
        if (isNaN(+transformedText)) break;

        if (!item.add_no_space) transformedText += ' ';

        if (+transformedText === 0) transformedText = item.text.zero_text[this.currentLanguage];
        else if (+transformedText === 1 ) transformedText += item.text.singular_text[this.currentLanguage];
        else if (+transformedText > 1) transformedText += item.text.plural_text[this.currentLanguage];
        break;
      case FORMAT_PIPES.HUMANIZE:
        transformedText = this.humanizePipe.transform(text);
        break;
      case FORMAT_PIPES.MAP_ENUM:
        transformedText = item.mappings[text][this.currentLanguage] ?
          item.mappings[text][this.currentLanguage] : item.mappings[text];
        break;
      case FORMAT_PIPES.WEIGHT_UNIT:
        transformedText = this.weightUnitPipe.transform(+text, item.current_unit, item.format, metricUnit);
        break;
      case FORMAT_PIPES.FULL_NAME:
        const flattenedData: any = this.tableUtilsService.flattenObject(data);
        transformedText = this.fullNamePipe.transform({
          firstName: flattenedData[item.first_name_key],
          lastName: flattenedData[item.last_name_key],
          deactivatedAt: flattenedData[item.append_deactivated?.deactivated_key],
        });
        break;
      case FORMAT_PIPES.HOS_DURATION:
        transformedText = this.hosDuration.transform(+text, item.show_seconds);
        break;
      case FORMAT_PIPES.DUTY_STATUS_DURATION:
        transformedText = '' + this.dutyStatusDurationPipe.transform(transformedText);
        break;
      case FORMAT_PIPES.ADD_NUMBERS_FROM_KEYS:
        let total: number = +transformedText;
        item.keys.forEach((key: string): void => {
          total += data[key];
        });
        transformedText = '' + total;
        break;
      case FORMAT_PIPES.CYCLE:
        transformedText = getCycle(transformedText).name;
        break;
      case FORMAT_PIPES.STATE_PIPE:
        transformedText = this.statePipe.transform(transformedText);
        break;
      case FORMAT_PIPES.FILE_SIZE:
        transformedText = this.fileSizePipe.transform(transformedText);
        break;
      case FORMAT_PIPES.FUEL_ECONOMY:
        transformedText = this.fuelEconomyPipe.transform
          (parseFloat(transformedText), metricUnit, this.fuelEconomyUnit, item.fraction_size).toString();
        break;
      case FORMAT_PIPES.ACCELERATION:
        const eventType: string = data[item.event_key];
        transformedText = formatAcceleration(transformedText, eventType, this.metricUnits);
        break;
      case FORMAT_PIPES.EV_ECONOMY:
        transformedText = this.evEconomyPipe.transform(
          parseFloat(transformedText), metricUnit, item.reverse, item.decimal_places,
        );
        break;
      case FORMAT_PIPES.SECONDARY_KEY:
        transformedText = this.secondaryKeyPipe.transform(data, item.keys, columnConfig?.null_text);
        break;
      case FORMAT_PIPES.METRIC_DISTANCE_UNITS:
        transformedText += ` / ${this.metricDistanceUnitsPipe.transform(this.metricUnits)}`;
        break;
      case FORMAT_PIPES.CALCULATE_DISTANCE_PER_HUNDRED_UNIT:
        const unit: string = metricUnit ? 'km' : 'mi';
        const extraText: string = ` ${transformedText} 100 ${unit}`; // calculating distance (transformedText) per 100 unit
        transformedText =
          calculateDistancePerHundredUnit(data[item.event_key], data[item.distance_key], this.metricUnits) + extraText;
        break;
      case FORMAT_PIPES.SPEEDING_RATE:
        const distance: string = this.durationPipe.transform(+transformedText, false, true, false, false, false);
        const speedingTime: number = +transformedText;
        const drivingTime: number = data[item.driving_time_key];
        const rate: number = speedingTime / drivingTime;
        transformedText = distance + ' ' + `(${this.percentPipe.transform(rate, item.format)})`;
        break;
      case FORMAT_PIPES.PHONE_NUMBER_FORMAT:
        transformedText = phoneNumberToIntlFormat(data[item.phone_key], data[item.phone_country_code_key]);
        break;
      case FORMAT_PIPES.BOOLEAN_YES_NO:
        transformedText = transformedText ? $localize`Yes` : $localize`No`;
        break;
      default:
        logger.error(`Unrecognized format pipe: ${JSON.stringify(item)}`);
      }
    }
    return transformedText;
  }
}
